import { computed } from "mobx";
import { useMemo } from "react";
import { useFilterSettingsStore, useLeaseCompStore, useLocationStore, usePipedriveStore, useSaleCompStore } from "..";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import INearestLocation, { NearestLocationType } from "../interfaces/INearestLocation";
import useCircleWithRadius from "./useCircleWithRadius";
import useLeaseCompsWithFocusArea from "./useLeaseCompsWithFocusArea";
import useLeaseCompsWithGlobalFilters from "./useLeaseCompsWithGlobalFilters";
import useLocationsWithFocusArea from "./useLocationsWithFocusArea";
import useLocationsWithGlobalFilters from "./useLocationsWithGlobalFilters";
import useSaleCompsWithFocusArea from "./useSaleCompsWithFocusArea";
import useSaleCompsWithGlobalFilters from "./useSaleCompsWithGlobalFilters";
import { toMap } from "../utils/utils";
import _ from "lodash";

export default function useNearestLocations(coordinates: IGeoCoordinate, radius: number): INearestLocation[] {
    const pipedriveStore = usePipedriveStore();
    const locationStore = useLocationStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const filterSettingsStore = useFilterSettingsStore();
    const circle = useCircleWithRadius(coordinates, radius)!;
    const locations = useLocationsWithFocusArea(useLocationsWithGlobalFilters(locationStore.locations));
    const leaseComps = useLeaseCompsWithFocusArea(useLeaseCompsWithGlobalFilters(leaseCompStore.leaseComps));
    const saleComps = useSaleCompsWithFocusArea(useSaleCompsWithGlobalFilters(saleCompStore.saleComps));
    const { includeLeaseComps, includeSaleComps } = filterSettingsStore.filters.companySettings;

    const companyIdsInfos = useMemo(() => {
        const companyIdsLocationCounts = _.countBy(locations, l => l.pipedriveCompanyId);
        const companyIdsLeaseCompCounts = includeLeaseComps ? _.countBy(leaseComps, lc => lc.pipedriveCompanyId) : {};
        const companyIdsSaleCompCounts = includeSaleComps ? _.countBy(saleComps.flatMap(sc =>
            _.uniq([sc.pipedriveBuyerId, sc.pipedriveSellerId, sc.pipedriveBrokerageId].filter(id => id !== null))
        ), id => id) : {};

        return toMap(pipedriveStore.organizations, o => o.id, o => ({
            name: o.name,
            locationCount: (companyIdsLocationCounts[o.id] ?? 0) + (companyIdsLeaseCompCounts[o.id] ?? 0) + (companyIdsSaleCompCounts[o.id] ?? 0),
        }));
    }, [pipedriveStore.organizations, locations, includeLeaseComps, leaseComps, includeSaleComps, saleComps]);

    const locationsInRadius = useMemo(() => computed(() => locations.filter(l =>
        circle.isPointInside(l.centerPoint)
    )), [circle, locations]).get();

    const leaseCompsInRadius = useMemo(() => computed(() => leaseComps.filter(lc =>
        circle.isPointInside(lc.centerPoint)
    )), [circle, leaseComps]).get();

    const saleCompsInRadius = useMemo(() => computed(() => saleComps.filter(sc =>
        circle.isPointInside(sc.centerPoint)
    )), [circle, saleComps]).get();

    const companyIdsWithLocationsInRadius = useMemo(() => new Set(locationsInRadius.map(l => l.pipedriveCompanyId)), [locationsInRadius]);

    return useMemo(() => _.orderBy([
        ...locationsInRadius.map(loc => ({
            rowId: `location-${loc.id}`,
            type: NearestLocationType.Location,
            entityId: loc.id,
            pipedriveCompanyId: loc.pipedriveCompanyId,
            companyName: companyIdsInfos.get(loc.pipedriveCompanyId)?.name ?? "",
            companyLocationCount: companyIdsInfos.get(loc.pipedriveCompanyId)?.locationCount ?? 0,
            address: loc.address,
            siteName: loc.siteName,
            centerPoint: loc.centerPoint,
            distance: circle.getDistanceFromCenter(loc.centerPoint),
        })),
        ...(includeLeaseComps
            ? leaseCompsInRadius.filter(lc => lc.pipedriveCompanyId !== null && companyIdsWithLocationsInRadius.has(lc.pipedriveCompanyId)).map(lc => ({
                rowId: `leasecomp-${lc.id}`,
                type: NearestLocationType.LeaseComp,
                entityId: lc.id,
                pipedriveCompanyId: lc.pipedriveCompanyId!,
                companyName: companyIdsInfos.get(lc.pipedriveCompanyId!)?.name ?? "",
                companyLocationCount: companyIdsInfos.get(lc.pipedriveCompanyId!)?.locationCount ?? 0,
                address: lc.address,
                siteName: null,
                centerPoint: lc.centerPoint,
                distance: circle.getDistanceFromCenter(lc.centerPoint),
            }))
            : []),
        ...(includeSaleComps
            ? saleCompsInRadius.flatMap(sc => [
                ...(sc.pipedriveBuyerId !== null && companyIdsWithLocationsInRadius.has(sc.pipedriveBuyerId) ? [{
                    rowId: `salecomp-${sc.id}-buyer-${sc.pipedriveBuyerId}`,
                    type: NearestLocationType.SaleComp,
                    entityId: sc.id,
                    pipedriveCompanyId: sc.pipedriveBuyerId,
                    companyName: companyIdsInfos.get(sc.pipedriveBuyerId)?.name ?? "",
                    companyLocationCount: companyIdsInfos.get(sc.pipedriveBuyerId)?.locationCount ?? 0,
                    address: sc.address,
                    siteName: null,
                    centerPoint: sc.centerPoint,
                    distance: circle.getDistanceFromCenter(sc.centerPoint),
                }] : []),
                ...(sc.pipedriveSellerId !== null && companyIdsWithLocationsInRadius.has(sc.pipedriveSellerId) ? [{
                    rowId: `salecomp-${sc.id}-seller-${sc.pipedriveSellerId}`,
                    type: NearestLocationType.SaleComp,
                    entityId: sc.id,
                    pipedriveCompanyId: sc.pipedriveSellerId,
                    companyName: companyIdsInfos.get(sc.pipedriveSellerId)?.name ?? "",
                    companyLocationCount: companyIdsInfos.get(sc.pipedriveSellerId)?.locationCount ?? 0,
                    address: sc.address,
                    siteName: null,
                    centerPoint: sc.centerPoint,
                    distance: circle.getDistanceFromCenter(sc.centerPoint),
                }] : []),
                ...(sc.pipedriveBrokerageId !== null && companyIdsWithLocationsInRadius.has(sc.pipedriveBrokerageId) ? [{
                    rowId: `salecomp-${sc.id}-brokerage-${sc.pipedriveBrokerageId}`,
                    type: NearestLocationType.SaleComp,
                    entityId: sc.id,
                    pipedriveCompanyId: sc.pipedriveBrokerageId,
                    companyName: companyIdsInfos.get(sc.pipedriveBrokerageId)?.name ?? "",
                    companyLocationCount: companyIdsInfos.get(sc.pipedriveBrokerageId)?.locationCount ?? 0,
                    address: sc.address,
                    siteName: null,
                    centerPoint: sc.centerPoint,
                    distance: circle.getDistanceFromCenter(sc.centerPoint),
                }] : []),
            ])
            : []),
    ], l => l.distance, "asc"), [locationsInRadius, includeLeaseComps, leaseCompsInRadius, includeSaleComps, saleCompsInRadius, companyIdsInfos, companyIdsWithLocationsInRadius, circle]);
}
